import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

const CustomDropdown = ({ 
  value, 
  onChange, 
  options, 
  placeholder = 'Select an option...', 
  className = '',
  displayValue = null,
  multiSelect = false,
  selectedValues = [],
  onDone = null,
  doneButtonText = 'Done',
  testId = undefined
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const buttonRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      
      // Position dropdown
      if (spaceBelow < 250 && rect.top > spaceBelow) {
        // Open upward
        setDropdownStyle({
          position: 'fixed',
          left: `${rect.left}px`,
          bottom: `${window.innerHeight - rect.top}px`,
          width: `${rect.width}px`,
          maxHeight: '240px'
        });
      } else {
        // Open downward
        setDropdownStyle({
          position: 'fixed',
          left: `${rect.left}px`,
          top: `${rect.bottom + 4}px`,
          width: `${rect.width}px`,
          maxHeight: '240px'
        });
      }
    }
  }, [isOpen]);
  
  const handleSelect = (optionValue) => {
    if (multiSelect) {
      // Don't close dropdown in multi-select mode
      onChange(optionValue);
    } else {
      onChange(optionValue);
      setIsOpen(false);
    }
  };
  
  const getDisplayText = () => {
    if (displayValue) return displayValue;
    if (multiSelect && selectedValues.length > 0) {
      return `${selectedValues.length} selected`;
    }
    if (!value) return placeholder;
    const option = options.find(opt => opt.value === value);
    return option?.label || value;
  };
  
  const dropdownMenu = isOpen && (
    <>
      <div 
        className="fixed inset-0 z-40" 
        onClick={() => setIsOpen(false)}
        onWheel={(e) => e.stopPropagation()}
      />
      <div 
        style={{
          ...dropdownStyle,
          overflowY: 'auto',
          overflowX: 'hidden',
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
        className="z-50 bg-white border border-gray-300 rounded-md shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        role="listbox"
        data-testid={testId ? `${testId}-menu` : undefined}
      >
        <div style={{ overflowY: 'auto', maxHeight: '200px' }}>
          {options.map(option => {
            const isDisabled = option.disabled || false;
            const isSelected = multiSelect && selectedValues.includes(option.value);
            
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) {
                    handleSelect(option.value);
                  }
                }}
                disabled={isDisabled}
                className={`w-full px-3 py-2 text-sm text-left cursor-pointer transition-colors flex items-center justify-between ${
                  isDisabled 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : isSelected 
                      ? 'bg-blue-50 hover:bg-blue-100' 
                      : 'hover:bg-blue-50'
                }`}
              >
                <span>{option.label}</span>
                {multiSelect && isSelected && !isDisabled && (
                  <Check className="w-4 h-4 text-blue-600" />
                )}
              </button>
            );
          })}
        </div>
        {multiSelect && (
          <div className="border-t border-gray-200 p-2">
            {selectedValues.length > 0 && onDone ? (
              <button
                type="button"
                data-testid={testId ? `${testId}-done` : undefined}
                onClick={() => {
                  if (onDone) {
                    onDone();
                  }
                  setIsOpen(false);
                }}
                className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded cursor-pointer transition-colors"
              >
                {doneButtonText}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded cursor-pointer transition-colors"
              >
                Done
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
  
  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md text-left bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer flex items-center justify-between"
      >
        <span className={value || (multiSelect && selectedValues.length > 0) ? 'text-gray-900' : 'text-gray-400'}>
          {getDisplayText()}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {createPortal(dropdownMenu, document.body)}
    </div>
  );
};

export default CustomDropdown;


